/**
 * Einzelfreigabe: POST /tasks/:id/approve (E1).
 *
 * Stages ONE approval at task.meta.approval; the status is left alone, so the caller
 * reads `approval.staged`, not a status change. Not `complete` - a staged task refuses
 * that with 409 task_release_required, because the batch closes at the Gesamtfreigabe.
 *
 * Mock mode stages into the worklist cache instead of calling the api (the useCreateTask
 * split). Every mock Eingang task is awaiting_approval, never terminal, so an inert
 * approve would leave the footer permanently short of full progress and the Gesamtfreigabe
 * button would never appear: the whole ladder is unreachable without this leg.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TaskSummary } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';
import type { EingangTasksResult } from './useEingangTasks';

export function useApproveTask(commId: string | null) {
  const live = isLiveData();
  const qc = useQueryClient();
  const key = qk.inbox({ eingang: true, comm_id: commId });
  return useMutation<TaskSummary | null, Error, string>({
    mutationFn: (taskId) => (live ? apiClient.tasks.approve(taskId) : Promise.resolve(null)),
    onSuccess: (_res, taskId) => {
      if (live) {
        void qc.invalidateQueries({ queryKey: key });
        return;
      }
      // The mock leg: the same marker the server writes, so the column and the footer
      // read `approval.staged` in both modes and the Maske needs no mock branch at all.
      qc.setQueryData<EingangTasksResult>(key, (prev) =>
        prev === undefined
          ? prev
          : {
              ...prev,
              rows: prev.rows.map((row) =>
                row.id !== taskId
                  ? row
                  : {
                      ...row,
                      approval: {
                        staged: true,
                        approved_by: null,
                        approved_at: new Date().toISOString(),
                        held_action: null,
                      },
                    },
              ),
            },
      );
    },
  });
}
